class TolstoySlider extends HTMLElement {
  connectedCallback() {
    if (this._ready) return

    customElements.whenDefined('splide-carousel').then(() => {
      this.waitForSliders()
    })
  }

  waitForSliders(attempts = 0) {
    this.initCarousel = this.querySelector('.tolstoy__init-slider')
    this.mainCarousel = this.querySelector('.tolstoy__main-slider')
    this.overlay = this.querySelector('.tolstoy__overlay')
    this.initSplide = this.initCarousel?.slider
    this.mainSplide = this.mainCarousel?.slider

    if (this.initSplide && this.mainSplide) {
      this.init()
      return
    }

    if (attempts < 20) {
      requestAnimationFrame(() => this.waitForSliders(attempts + 1))
    }
  }

  init() {
    if (this._ready) return

    this._ready = true
    this.muted = false
    this.isOpen = false

    this.syncMutedState()

    this.getVideos().forEach((video) => {
      video.playsInline = true
      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')
    })

    this.bindInitOpens()
    this.bindInitArrows()
    this.bindMainNav()
    this.bindSoundButtons()
    this.bindOverlayDismiss()
    this.bindAddToCart()
    this.bindViewportObserver()
    this.syncInitProgress()

    this.mainSplide.on('moved', () => {
      if (this.isOpen && !this._isOpening) {
        requestAnimationFrame(() => {
          this.playSlide()
          this.updateMainNavState()
        })
      }
    })

    this.initSplide.on('move', () => this.syncInitProgress())
    this.initSplide.on('moved', () => {
      this.syncInitProgress()
      this.updateInitNavState()
    })
    this.initSplide.on('mounted', () => {
      this.syncInitProgress()
      this.updateInitNavState()
    })
  }

  bindInitOpens() {
    this.querySelectorAll('[data-tolstoy-open]').forEach((button) => {
      button.addEventListener('click', () => {
        this.openMain(Number(button.dataset.tolstoyOpen))
      })
    })
  }

  bindInitArrows() {
    this.querySelectorAll('[data-init-prev]').forEach((button) => {
      button.addEventListener('click', () => this.initSplide.go('<'))
    })

    this.querySelectorAll('[data-init-next]').forEach((button) => {
      button.addEventListener('click', () => this.initSplide.go('>'))
    })
  }

  bindMainNav() {
    this.overlay?.addEventListener('click', (event) => {
      if (event.target.closest('[data-main-prev]')) {
        event.stopPropagation()
        this.mainSplide.go('<')
      }

      if (event.target.closest('[data-main-next]')) {
        event.stopPropagation()
        this.mainSplide.go('>')
      }

      if (event.target.closest('[data-tolstoy-close]')) {
        event.stopPropagation()
        this.closeMain()
      }
    })
  }

  bindSoundButtons() {
    this.overlay?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sound]')
      if (!button) return

      event.preventDefault()
      event.stopPropagation()
      this.toggleSound()
    })
  }

  bindOverlayDismiss() {
    this.overlay?.addEventListener('click', (event) => {
      if (event.target.closest('.tolstoy__main-panel')) return

      if (this.isMobileOverlay()) {
        if (event.target === this.overlay) this.closeMain()
        return
      }

      this.closeMain()
    })

    this._onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.closeMain()
    }

    document.addEventListener('keydown', this._onKeydown)
  }

  bindViewportObserver() {
    if (!('IntersectionObserver' in window)) return

    this._viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) this.handleLeftViewport()
        })
      },
      { threshold: 0 }
    )

    this._viewportObserver.observe(this)
  }

  handleLeftViewport() {
    if (this.isOpen) this.closeMain()
  }

  bindAddToCart() {
    this._atcResetTimers = new Map()
    this._checkmarkTemplate = this.querySelector('[data-tolstoy-atc-check-template]')

    this.querySelectorAll('product-form[data-tolstoy-atc]').forEach((form) => {
      form.addEventListener('tolstoy:added', () => {
        this.showAddedState(form)
      })
    })
  }

  showAddedState(productForm) {
    const button = productForm.querySelector('[data-tolstoy-atc-button]')
    const label = productForm.querySelector('[data-tolstoy-atc-label]')
    const icon = productForm.querySelector('[data-tolstoy-atc-icon]')

    if (!button || !label || !icon) return

    const existingTimer = this._atcResetTimers.get(button)
    if (existingTimer) clearTimeout(existingTimer)

    if (!button.dataset.tolstoyOriginalLabel) {
      button.dataset.tolstoyOriginalLabel = label.textContent.trim()
      button.dataset.tolstoyOriginalIcon = icon.innerHTML
    }

    button.setAttribute('aria-disabled', 'true')
    button.disabled = true
    button.classList.add('tolstoy-atc--added')
    label.textContent = this.dataset.addedLabel || 'Added to cart'

    icon.innerHTML = ''
    const checkmark = this._checkmarkTemplate?.content?.firstElementChild?.cloneNode(true)
    if (checkmark) icon.appendChild(checkmark)

    const resetTimer = setTimeout(() => {
      this.resetAddedState(button, label, icon)
      this._atcResetTimers.delete(button)
    }, 5000)

    this._atcResetTimers.set(button, resetTimer)
  }

  resetAddedState(button, label, icon) {
    button.removeAttribute('aria-disabled')
    button.disabled = false
    button.classList.remove('tolstoy-atc--added')
    label.textContent = button.dataset.tolstoyOriginalLabel || this.dataset.buyLabel || ''
    icon.innerHTML = button.dataset.tolstoyOriginalIcon || ''
  }

  syncInitProgress() {
    const progressBar = this.querySelector('.tolstoy__init-progress-bar')
    if (!progressBar || !this.initSplide) return

    const end = this.initSplide.Components.Controller.getEnd() + 1
    const index = this.initSplide.index
    const progress = end > 1 ? index / (end - 1) : 1

    progressBar.style.width = `${Math.min(progress, 1) * 100}%`
  }

  getVideos() {
    return [...this.mainCarousel.querySelectorAll('video')]
  }

  getActiveSlide() {
    if (!this.mainCarousel) return null

    return (
      this.mainCarousel.querySelector('.splide__slide.is-visible') ||
      this.mainCarousel.querySelector('.splide__slide.is-active:not([aria-hidden="true"])') ||
      null
    )
  }

  getSlideVideo(slide) {
    if (!slide) return null

    const video = slide.querySelector('video')
    if (!video) return null

    if (this.getVideoSourceUrl(video)) return video

    const logicalIndex = slide.dataset.tolstoySlide
    if (logicalIndex == null) return video

    const sourceSlide = this.mainCarousel.querySelector(
      `.splide__slide[data-tolstoy-slide="${logicalIndex}"]:not(.splide__slide--clone)`
    )
    const sourceVideo = sourceSlide?.querySelector('video')
    if (!sourceVideo || !this.getVideoSourceUrl(sourceVideo)) return video

    const sources = sourceVideo.querySelectorAll('source')

    if (sources.length) {
      video.replaceChildren(...[...sources].map((item) => item.cloneNode(true)))
    } else if (sourceVideo.src) {
      video.src = sourceVideo.src
    }

    if (sourceVideo.poster) video.poster = sourceVideo.poster
    video.load()

    return video
  }

  getVideoSourceUrl(video) {
    if (!video) return ''

    return (
      video.currentSrc ||
      video.querySelector('source')?.getAttribute('src') ||
      video.getAttribute('src') ||
      ''
    )
  }

  getVideoPovs() {
    return [...this.mainCarousel.querySelectorAll('.tolstoy__video-pov')]
  }

  getSoundButtons() {
    return [...this.mainCarousel.querySelectorAll('[data-sound]')]
  }

  setVideoMuted(video, muted) {
    video.muted = muted

    if (muted) {
      video.setAttribute('muted', '')
    } else {
      video.removeAttribute('muted')
    }
  }

  syncMutedState() {
    this.getVideos().forEach((video) => {
      this.setVideoMuted(video, this.muted)
    })

    this.getSoundButtons().forEach((button) => {
      button.classList.toggle('sound-off', this.muted)
    })
  }

  goToSlide(index, animate = true) {
    const splide = this.mainSplide

    if (animate) {
      splide.go(index)
      return
    }

    const { Controller, Move, Transition, Slides } = splide.Components
    const { speed, waitForTransition } = splide.options

    Transition.cancel()
    splide.options.speed = 0
    splide.options.waitForTransition = false

    Controller.setIndex(index)
    Move.jump(index)
    Slides.update()

    splide.options.speed = speed
    splide.options.waitForTransition = waitForTransition
  }

  pauseAll() {
    this.getVideoPovs().forEach((videoPov) => {
      videoPov.classList.remove('playing')
    })

    this.getVideos().forEach((video) => {
      video.pause()
      video.currentTime = 0
    })
  }

  playSlide() {
    this.pauseAll()

    const slide = this.getActiveSlide()
    const video = this.getSlideVideo(slide)

    if (!video) return

    const startPlayback = () => {
      if (this.getActiveSlide() !== slide) return

      this.setVideoMuted(video, this.muted)
      slide.querySelector('.tolstoy__video-pov')?.classList.add('playing')
      video.play().catch(() => {})
    }

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback()
      return
    }

    video.addEventListener('canplay', startPlayback, { once: true })

    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
      video.load()
    }
  }

  toggleSound() {
    this.muted = !this.muted
    this.syncMutedState()
  }

  getContentColumn() {
    const mainProduct = this.closest('.main-product')
    if (!mainProduct) return null

    return (
      mainProduct.querySelector('.main-product__column--content') ||
      mainProduct.querySelector('.main-product__column--content-basic')
    )
  }

  isMobileOverlay() {
    return window.matchMedia('(max-width: 920px)').matches
  }

  mountOverlay() {
    this._overlayMode = this.isMobileOverlay() ? 'mobile' : 'desktop'

    if (this._overlayMode === 'mobile') {
      if (this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay)
      }

      this.resetOverlayPosition()
      return
    }

    const column = this.getContentColumn()
    if (!column) return

    if (this.overlay.parentElement !== column) {
      column.appendChild(this.overlay)
    }

    column.classList.add('tolstoy-column--open')
    this.positionOverlay()
  }

  unmountOverlay() {
    const column = this.getContentColumn()

    if (this._overlayMode === 'mobile') {
      if (this.overlay.parentElement === document.body) {
        this.appendChild(this.overlay)
      }
    } else {
      column?.classList.remove('tolstoy-column--open')

      if (this.overlay.parentElement !== this) {
        this.appendChild(this.overlay)
      }
    }

    this.resetOverlayPosition()
    this._overlayMode = null
  }

  positionOverlay() {
    if (!this.overlay) return

    if (this.isMobileOverlay()) {
      this.resetOverlayPosition()
      return
    }

    const column = this.getContentColumn()
    if (!column) return

    const columnRect = column.getBoundingClientRect()

    this.overlay.style.top = '0'
    this.overlay.style.right = 'auto'
    this.overlay.style.bottom = '0'
    this.overlay.style.left = `${columnRect.left}px`
    this.overlay.style.width = `${columnRect.width}px`
    this.overlay.style.height = '100vh'
    this.overlay.style.height = '100dvh'
  }

  resetOverlayPosition() {
    this.overlay.style.top = ''
    this.overlay.style.right = ''
    this.overlay.style.bottom = ''
    this.overlay.style.left = ''
    this.overlay.style.width = ''
    this.overlay.style.height = ''
  }

  bindOverlayListeners() {
    this._onReposition = () => {
      if (this.isOpen) this.positionOverlay()
    }

    window.addEventListener('resize', this._onReposition)
    window.addEventListener('scroll', this._onReposition, { passive: true })
  }

  unbindOverlayListeners() {
    if (!this._onReposition) return

    window.removeEventListener('resize', this._onReposition)
    window.removeEventListener('scroll', this._onReposition)
  }

  openMain(index) {
    this.isOpen = true
    this._isOpening = true

    this.mountOverlay()
    this.bindOverlayListeners()
    this.overlay.classList.add('tolstoy__overlay--open')
    this.overlay.setAttribute('aria-hidden', 'false')
    this.classList.add('tolstoy--open')
    document.body.classList.add('tolstoy-slider-open')

    requestAnimationFrame(() => {
      this.mainSplide.refresh()
      this.goToSlide(index, false)

      requestAnimationFrame(() => {
        this._isOpening = false
        this.playSlide()
        this.updateMainNavState()
      })
    })
  }

  closeMain() {
    this.isOpen = false
    this.pauseAll()
    this.muted = false
    this.syncMutedState()
    this.unbindOverlayListeners()
    this.overlay.classList.remove('tolstoy__overlay--open')
    this.overlay.setAttribute('aria-hidden', 'true')
    this.classList.remove('tolstoy--open')
    document.body.classList.remove('tolstoy-slider-open')
    this.unmountOverlay()

    this.goToSlide(0, false)
  }

  updateMainNavState() {
    const prev = this.overlay?.querySelector('[data-main-prev]')
    const next = this.overlay?.querySelector('[data-main-next]')

    if (!prev || !next) return

    prev.disabled = false
    next.disabled = false
  }

  updateInitNavState() {
    const atStart = this.initSplide.index === 0
    const atEnd = this.initSplide.index >= this.initSplide.length - 1

    this.querySelectorAll('[data-init-prev]').forEach((button) => {
      button.disabled = atStart
    })

    this.querySelectorAll('[data-init-next]').forEach((button) => {
      button.disabled = atEnd
    })
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown)
    this.unbindOverlayListeners?.()
    this._viewportObserver?.disconnect()

    this._atcResetTimers?.forEach((timer) => clearTimeout(timer))
    this._atcResetTimers?.clear()

    if (this.isOpen) {
      this.getContentColumn()?.classList.remove('tolstoy-column--open')
      this.unmountOverlay()
    }

    document.body.classList.remove('tolstoy-slider-open')
  }
}

customElements.define('tolstoy-slider', TolstoySlider)
